import { publicView, standings, type Match, type Seat } from '@ubs/rules';
import type { GameEvent, PublicSeat, ViewMessage } from './index.js';

/**
 * İki durum arasındaki farkı olaylara çevirir.
 *
 * Turların kaydı kural motorunun kendi `history`'sinden türüyor, elle yeniden
 * inşa edilmiyor — animasyonla gerçek durumun ayrışması yapısal olarak
 * imkânsız. Faz geçişleri (koz, gömme, alışveriş) `history`'de yok çünkü el
 * içindeki kart akışına ait değiller; onlar fazın kendisinden okunuyor.
 *
 * Burada, paylaşılan pakette duruyor çünkü İKİ yer bunu üretiyor: sunucudaki
 * masa (online) ve tarayıcıdaki yerel oyun (botlara karşı). Kopyalansaydı
 * ikisi zamanla ayrışır, masa bileşeni moda göre farklı davranırdı.
 */
export function deriveEvents(before: Match, after: Match): GameEvent[] {
  const events: GameEvent[] = [];

  const consumed = before.deal?.history.length ?? 0;
  const sameDeal = after.dealIndex === before.dealIndex;
  // El bittiyse son turların kaydı `lastDeal`'da kaldı.
  const source = sameDeal ? after.deal : after.lastDeal;

  for (const entry of source?.history.slice(consumed) ?? []) {
    events.push(
      entry.kind === 'play'
        ? { kind: 'play', seat: entry.seat, card: entry.card }
        : { kind: 'trickEnd', winner: entry.winner, tricks: [...entry.tricks] },
    );
  }

  if (sameDeal) {
    // Alışverişte tamamlanan her adım ayrı bir uçuş animasyonu.
    const doneBefore = before.exchange?.done.length ?? 0;
    for (const transfer of after.exchange?.done.slice(doneBefore) ?? []) {
      events.push({
        kind: 'exchangeStep',
        from: transfer.from,
        to: transfer.to,
        count: transfer.given.length,
      });
    }
    if (before.phase === 'exchange' && after.phase !== 'exchange') {
      events.push({ kind: 'exchangeDone' });
    }
    if (before.phase === 'trump' && after.phase === 'bury') {
      events.push({
        kind: 'trumpChosen',
        seat: after.deal!.role8,
        suit: after.deal!.trump!,
      });
    }
    if (before.phase === 'bury' && after.phase === 'play') {
      events.push({ kind: 'buryDone', seat: after.deal!.role8 });
    }
    return events;
  }

  events.push({ kind: 'dealEnd', result: after.dealResults[after.dealResults.length - 1]! });

  if (after.phase === 'matchOver') {
    events.push({ kind: 'matchOver', standings: standings(after) });
    return events;
  }

  /**
   * Sıra önemli: önce yeni kartlar dağıtılıyor, SONRA alışveriş açılıyor.
   * Alışveriş yeni ellerin üzerinde yapılıyor — ters sırada istemci henüz
   * görmediği kartların takas edildiğini gösterirdi.
   */
  events.push({ kind: 'dealStart', dealIndex: after.dealIndex, role8: after.deal!.role8 });

  if (after.exchange) {
    events.push({
      kind: 'exchangeStart',
      plan: after.exchange.queue.flatMap((step) =>
        step.gives.map((g) => ({ from: step.from, to: g.to, count: g.count })),
      ),
    });
  }

  return events;
}

/** Oyuncuya gidecek mesajı kurar. Online ve yerel modda aynı şekil. */
export function buildViewMessage(
  match: Match,
  seat: Seat,
  extra: {
    seq: number;
    seats: PublicSeat[];
    deadline: number | null;
    deadlineTotal: number | null;
  },
): ViewMessage {
  return {
    ...publicView(match, seat),
    seq: extra.seq,
    you: seat,
    seats: extra.seats,
    deadline: extra.deadline,
    deadlineTotal: extra.deadlineTotal,
  };
}
